/**
 * Contentful stores internal links as canonical production URLs
 * (`https://examenbundel.nl/veelgestelde-vragen`). Outside production those are
 * not recognised as internal, so every CMS-authored link — footer, navigation,
 * category CTAs — navigated the visitor straight off acceptance and onto the
 * live shop (EXAMS-138 QA).
 *
 * Treat every Examenbundel host as internal, whichever environment serves the
 * page: the canonical domain and any subdomain of it (`acc.`, `test.`), plus
 * whatever `APP_URL` this build was given.
 */
const INTERNAL_HOST = /(^|\.)examenbundel\.nl$/i;

const HAS_PROTOCOL = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * A leading scheme, i.e. a colon reached without passing a `/`, `?` or `#`:
 * `mailto:`, `tel:`. Testing for a colon anywhere also rejected the catalogue's
 * own links — `/producten?list:examen_onderwijstype=havo` — so every schoolniveau
 * item in the navigation rendered as an external link and opened in a new tab
 * (EXAMS-138, Menno 25-08).
 */
const LEADING_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function appHost (): string | null {
  try {
    return process.env.APP_URL ? new URL(process.env.APP_URL).host : null;
  } catch {
    return null;
  }
}

/**
 * True for links that should render as a `<nuxt-link>`: anything without a
 * protocol (already a path) and absolute URLs pointing at this shop.
 *
 * `mailto:`/`tel:` carry no `//` and stay external, as before.
 */
export function isInternalUrl (url: string | null | undefined): boolean {
  if (!url) return false;
  if (!HAS_PROTOCOL.test(url)) return !LEADING_SCHEME.test(url);
  try {
    const { host } = new URL(url);
    return INTERNAL_HOST.test(host) || host === appHost();
  } catch {
    return false;
  }
}

/**
 * Strips this shop's origin off a link so it can be handed to vue-router.
 * Leaves relative links and genuinely external URLs untouched.
 */
export function toRelativeUrl (url: string | null | undefined): string {
  if (!url) return "/";
  if (!isInternalUrl(url) || !HAS_PROTOCOL.test(url)) return url;
  const { pathname, search, hash } = new URL(url);
  return `${pathname}${search}${hash}` || "/";
}
